import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { ExternalLink, Play, VideoOff, X } from 'lucide-react'
import styles from './MakeMode.module.css'
import MainHeader from '../../ReusableComponents/Header/MainHeader'
import {
    brandDeviceKeyFromDataKey,
    getConnectedDeviceNameWithVersion,
    getModeVideos,
} from '../../../hooks/useBrandConfig'
import {
    getYouTubeEmbedUrl,
    getYouTubeThumbnailUrl,
} from '../../../utils/youtube'
import { generateDeviceDataKey } from '../../ReusableComponents/ByteTransfer/utils'

// slug stays 'learn' — stable config identifier, shown as "Make"
const MODE_SLUG = 'learn'

const COURSES_URL = 'https://ai.tinkerbunker.com/courses/'
const PEECEE_V0_COURSES_URL =
    'https://www.tinkerbunker.com/courses/play-computer-robotics/'

const VideoCard = ({ video, onPlay }) => {
    const thumbnail = getYouTubeThumbnailUrl(video.youtube_url)

    return (
        <button type="button" className={styles.card} onClick={onPlay}>
            <span className={styles.thumbWrap}>
                {thumbnail ? (
                    <img
                        className={styles.thumb}
                        src={thumbnail}
                        alt=""
                        loading="lazy"
                    />
                ) : (
                    <span className={styles.thumbFallback} />
                )}
                <span className={styles.playBadge}>
                    <Play size={22} strokeWidth={2.4} fill="currentColor" />
                </span>
            </span>
            <span className={styles.cardTitle}>{video.display_name}</span>
            {video.description ? (
                <span className={styles.cardSubtitle}>{video.description}</span>
            ) : null}
        </button>
    )
}

const MakeMode = () => {
    const history = useHistory()
    const [activeVideo, setActiveVideo] = useState(null)

    const connectedDevice = sessionStorage.getItem('connectedDevice') || 'Ace'
    const deviceVersion = sessionStorage.getItem('deviceVersion') || '0.0.0'

    // same derivation the Selection screen uses to gate modes
    const deviceKey = useMemo(
        () =>
            brandDeviceKeyFromDataKey(
                generateDeviceDataKey(connectedDevice, deviceVersion || '1')
            ) || connectedDevice,
        [connectedDevice, deviceVersion]
    )

    const videos = useMemo(
        () => getModeVideos(MODE_SLUG, deviceKey),
        [deviceKey]
    )

    const coursesUrl =
        connectedDevice === 'Ace' && deviceVersion[0] === '0'
            ? PEECEE_V0_COURSES_URL
            : COURSES_URL

    const goBack = () => {
        sessionStorage.setItem('slideDirection', true)
        history.push('/Selection')
    }

    const openCourses = () => window.open(coursesUrl, '_blank')

    const activeEmbedUrl = activeVideo
        ? getYouTubeEmbedUrl(activeVideo.youtube_url, { autoplay: true })
        : null

    return (
        <div className={styles.page}>
            <MainHeader
                title={getConnectedDeviceNameWithVersion()}
                titlePosition="left"
                isGoBack={true}
                goBack={goBack}
                showAccountSetting={false}
            />

            <div className={styles.inner}>
                <div className={styles.headingRow}>
                    <h1 className={styles.heading}>Make</h1>
                    <button
                        type="button"
                        className={styles.coursesBtn}
                        onClick={openCourses}
                    >
                        <ExternalLink size={16} strokeWidth={2.2} />
                        Explore courses
                    </button>
                </div>

                {videos.length ? (
                    <>
                        <p className={styles.sub}>
                            Learning videos for your{' '}
                            {getConnectedDeviceNameWithVersion()}.
                        </p>
                        <div className={styles.cards}>
                            {videos.map((video) => (
                                <VideoCard
                                    key={video.slug}
                                    video={video}
                                    onPlay={() => setActiveVideo(video)}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <div className={styles.empty}>
                        <VideoOff size={44} strokeWidth={1.6} />
                        <p className={styles.emptyTitle}>
                            No learning videos yet
                        </p>
                        <p className={styles.emptyText}>
                            Videos for this device are on the way. In the
                            meantime you can explore the online courses.
                        </p>
                    </div>
                )}
            </div>

            {activeEmbedUrl ? (
                <div
                    className={styles.playerOverlay}
                    onClick={() => setActiveVideo(null)}
                >
                    <div
                        className={styles.player}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.playerBar}>
                            <span className={styles.playerTitle}>
                                {activeVideo.display_name}
                            </span>
                            <button
                                type="button"
                                className={styles.playerClose}
                                onClick={() => setActiveVideo(null)}
                                aria-label="Close video"
                            >
                                <X size={20} strokeWidth={2.4} />
                            </button>
                        </div>
                        <div className={styles.playerFrameWrap}>
                            <iframe
                                className={styles.playerFrame}
                                src={activeEmbedUrl}
                                title={activeVideo.display_name}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default MakeMode
